/**
 * Helpers for the `yyyy-MM-dd` scheduled dates sent to the charge APIs.
 *
 * `new Date('2026-08-27')` is parsed as UTC midnight, which is the previous day
 * in every negative-offset timezone, so date-only strings must never be handed
 * straight to `new Date`.
 */

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Local midnight for a `yyyy-MM-dd` string. */
export const parseScheduledDate = (value: string): Date | null => {
  const match = DATE_ONLY_PATTERN.exec(value.trim());
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `yyyy-MM-dd` for a date, using its local calendar day. */
export const formatScheduledDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Local midnight of the earliest day a charge can be scheduled for. */
export const getEarliestScheduledDay = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
};

export const isScheduledDateSelectable = (value: string): boolean => {
  const date = parseScheduledDate(value);
  return date !== null && date >= getEarliestScheduledDay();
};
